import React, { ReactNode, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { ColumnsType } from "antd/es/table/Table";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import { Deal } from "@mapmycustomers/shared/types/entity";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { Button, Modal } from "@mapmycustomers/ui";

import { DealListModalTitle } from "@app/component/DealListModalForCharts/DealListModalTitle";
import Table from "@app/component/Table";
import Path from "@app/enum/Path";
import ReportType from "@app/enum/ReportType";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import MapFilter from "@app/scene/reports/enums/MapFilter";
import downloadEntitiesAsCsv from "@app/scene/reports/utils/csv/downloadEntitiesAsCsv";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { PREVIEW_MODAL_LIMIT } from "@app/util/consts";

import { DownloadConfirmation } from "./DownloadConfirmation";
import styles from "./PreviewDealsListModel.module.scss";
import { getDeals, getLoading, getTotal } from "./store";
import { fetchDeals } from "./store/actions";

export const DEFAULT_PAGE_SIZE = 5;

interface Props {
  canDownload?: boolean;
  columns: ColumnsType<Deal>;
  deals: Deal[];
  fetchDeals: typeof fetchDeals.request;
  filter: FilterModel;
  isLoading: boolean;
  onDealClick: (deal: Deal) => void;
  onHide: () => void;
  stageName?: string;
  subtitle?: ReactNode | string;
  title?: string;
  total: number;
}

const PreviewDealsListModal = ({
  canDownload,
  columns,
  deals,
  fetchDeals,
  filter,
  isLoading,
  onDealClick,
  onHide,
  stageName,
  subtitle,
  title,
  total,
}: Props) => {
  const handlePageChange = useCallback(
    (page: number) => {
      fetchDeals({
        request: {
          filter,
          range: {
            endRow: page * DEFAULT_PAGE_SIZE,
            startRow: (page - 1) * DEFAULT_PAGE_SIZE,
          },
        },
      });
    },
    [fetchDeals, filter]
  );

  useEffect(() => {
    fetchDeals({ request: { filter } });
  }, [fetchDeals, filter]);

  const intl = useIntl();

  const handleRowEvents = useCallback(
    (deal: Deal) => ({
      onClick: () => onDealClick?.(deal),
    }),
    [onDealClick]
  );

  return (
    <Modal
      destroyOnClose
      footer={
        <div className={styles.footer}>
          <Button href={`${Path.DEAL}/${ViewMode.LIST}`} key="viewAll" target="_blank" type="link">
            {intl.formatMessage({
              id: "dealListModal.viewAllDeals",
              defaultMessage: "View all Deals",
              description: "View all Deals link on the Deal list modal",
            })}
          </Button>
          <Tooltip
            key="download"
            title={
              !canDownload
                ? intl.formatMessage({
                    id: "dealListModal.cannotExport",
                    defaultMessage:
                      "Your role does not have export access. Please contact your administrator to enable access",
                    description:
                      "Message shown on the list modal when the user does not have permission to export data",
                  })
                : null
            }
          >
            <span key="download">
              <DownloadConfirmation
                disabled={!canDownload}
                key="download"
                onConfirm={() =>
                  downloadEntitiesAsCsv(deals, ReportType.DEALS, MapFilter.ALL_DEALS)
                }
              >
                <Button disabled={!canDownload} type="primary">
                  {intl.formatMessage({
                    id: "dealListModal.downloadDealsButton",
                    defaultMessage: "Quick export",
                    description: "Download Deals button on the list modal",
                  })}
                </Button>
              </DownloadConfirmation>
            </span>
          </Tooltip>
        </div>
      }
      onCancel={onHide}
      title={
        <DealListModalTitle
          dealsTotal={total}
          stageName={stageName}
          subTitle={subtitle}
          title={title}
        />
      }
      visible
      width="clamp(300px, 70vw, 780px)"
    >
      <Table<Deal>
        clickableRows
        columns={columns}
        dataSource={deals}
        loading={isLoading}
        noHover
        onRow={handleRowEvents}
        pagination={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          onChange: handlePageChange,
          position: ["bottomCenter"],
          showSizeChanger: false,
          total: total < PREVIEW_MODAL_LIMIT ? total : PREVIEW_MODAL_LIMIT,
        }}
        rowKey="id"
        scrollable
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  deals: getDeals(state),
  isLoading: getLoading(state),
  total: getTotal(state),
});

const mapDispatchToProps = {
  fetchDeals: fetchDeals.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviewDealsListModal);
